import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Calendar } from "lucide-react";
import { CALENDLY_URL, IMAGES } from "@/lib/site-data";

const Hero = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden grain"
    >
      {/* Decorative blobs */}
      <div aria-hidden="true" className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-brand-periwinkle/40 blur-3xl pointer-events-none" />
      <div aria-hidden="true" className="absolute top-1/2 -right-24 w-[22rem] h-[22rem] rounded-full bg-brand-sage/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7 z-10">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="font-sans text-xs md:text-sm tracking-[0.25em] uppercase text-brand-sage mb-6"
            data-testid="hero-eyebrow"
          >
            People-centered consulting · est. 2023
          </motion.p>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="font-serif font-light text-[2.75rem] sm:text-6xl md:text-7xl leading-[1.02] tracking-tight text-brand-ink"
            data-testid="hero-headline"
          >
            People-centered{" "}
            <span className="italic font-normal text-brand-blue">strategy.</span>
            <br />
            Real-world{" "}
            <span className="underline-sketch">results.</span>
            <br />
            <span className="relative inline-block">
              <span className="relative z-10 italic font-normal text-brand-blue">
                Rooted in purpose.
              </span>
              <svg
                className="absolute -inset-3 w-[calc(100%+1.5rem)] h-[calc(100%+1.5rem)] pointer-events-none"
                viewBox="0 0 400 110"
                fill="none"
                preserveAspectRatio="none"
                aria-hidden="true"
              >
                <motion.path
                  initial={{ pathLength: 0 }}
                  animate={{ pathLength: 1 }}
                  transition={{ duration: 1.6, delay: 1.0, ease: "easeOut" }}
                  d="M 30 55 C 30 20, 100 8, 200 10 C 320 12, 380 30, 380 55 C 380 85, 310 102, 200 100 C 90 98, 30 88, 30 55"
                  stroke="#0d478a"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  fill="none"
                  opacity="0.55"
                />
              </svg>
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-8 max-w-xl text-lg md:text-xl text-brand-ink/75 leading-relaxed font-sans"
            data-testid="hero-subhead"
          >
            We don't just advise — we roll up our sleeves and work alongside
            mission-driven leaders to solve real problems, in real time. Hands-on
            support for organizations rooted in dignity, belonging, and impact.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.55 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-book-call-btn"
              className="group inline-flex items-center justify-center gap-2 bg-brand-blue text-brand-cream hover:bg-brand-ink transition-all duration-300 rounded-full px-8 py-4 font-sans font-medium text-base shadow-lg shadow-brand-blue/20 hover:shadow-xl hover:-translate-y-0.5"
            >
              <Calendar className="w-4 h-4" />
              Book a Discovery Call
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </a>
            <button
              onClick={() => scrollTo("services")}
              data-testid="hero-explore-services-btn"
              className="inline-flex items-center justify-center gap-2 bg-transparent text-brand-blue border border-brand-blue/40 hover:border-brand-blue hover:bg-brand-periwinkle/20 transition-all rounded-full px-8 py-4 font-sans font-medium text-base"
            >
              Explore Our Work
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1.1, delay: 0.3 }}
          className="lg:col-span-5 relative z-10"
        >
          <div className="relative aspect-[4/5] max-w-md mx-auto lg:max-w-none">
            <div className="absolute -inset-4 bg-brand-blue rounded-[2rem] rotate-3" aria-hidden="true" />
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-brand-periwinkle rounded-full blur-2xl opacity-70" aria-hidden="true" />
            <img
              src={IMAGES.heroCollaboration}
              alt="Diverse hands working together at a wooden table, symbolizing community-rooted collaboration"
              className="relative w-full h-full object-cover rounded-[2rem] shadow-2xl shadow-brand-blue/20 -rotate-1"
              data-testid="hero-image"
            />
            {/* Floating quote card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
              className="absolute -bottom-8 -left-4 md:-left-12 max-w-[16rem] bg-brand-cream border border-brand-blue/15 rounded-2xl px-5 py-4 shadow-xl"
            >
              <p className="font-serif italic text-sm md:text-base text-brand-ink leading-snug">
                "Solutions rooted in dignity, belonging, and impact."
              </p>
              <p className="mt-2 text-[10px] tracking-[0.2em] uppercase text-brand-sage">
                — Our mission
              </p>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="hidden md:flex absolute bottom-6 left-1/2 -translate-x-1/2 flex-col items-center gap-2 text-brand-sage" aria-hidden="true">
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll</span>
        <div className="w-px h-10 bg-brand-sage/40 animate-pulse" />
      </div>
    </section>
  );
};

export default Hero;
